import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Users, Baby, Plus, X, UserPlus, Save } from 'lucide-react';
import { Client, FamilyChild } from '@/types/shelter';

interface FamilyMembersSectionProps {
  client: Client;
  editedClient: Client | null;
  isEditing: boolean;
  onUpdateClient: (updatedClient: Client) => void;
  onSaveChanges?: () => void;
  isSaving?: boolean;
}

export const FamilyMembersSection: React.FC<FamilyMembersSectionProps> = ({
  client,
  editedClient,
  isEditing,
  onUpdateClient,
  onSaveChanges,
  isSaving = false
}) => {
  const [newChildName, setNewChildName] = useState('');
  const [newChildAge, setNewChildAge] = useState<string>('');
  const [partnerName, setPartnerName] = useState(editedClient?.partnerName || '');

  const children = editedClient?.children || client.children || [];
  const isHeadOfHousehold = editedClient?.isHeadOfHousehold || client.isHeadOfHousehold || false;

  const handleAddChild = () => {
    if (!newChildName.trim() || !editedClient) return;
    
    const newChild: FamilyChild = {
      name: newChildName.trim(),
      age: newChildAge ? parseInt(newChildAge, 10) : undefined
    };
    
    const updatedChildren = [...(editedClient.children || []), newChild];
    onUpdateClient({ ...editedClient, children: updatedChildren });
    setNewChildName('');
    setNewChildAge('');
  };

  const handleRemoveChild = (index: number) => {
    if (!editedClient) return;
    const updatedChildren = editedClient.children?.filter((_, i) => i !== index) || [];
    onUpdateClient({ ...editedClient, children: updatedChildren });
  };

  const handleToggleHeadOfHousehold = (checked: boolean) => {
    if (!editedClient) return;
    onUpdateClient({ ...editedClient, isHeadOfHousehold: checked });
  };

  const handleUpdatePartnerName = (name: string) => {
    setPartnerName(name);
    if (editedClient) {
      onUpdateClient({ ...editedClient, partnerName: name });
    }
  };

  return (
    <Card className="border-orange-200 bg-orange-50/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-orange-600" />
          Family Members
          <Badge variant="outline" className="ml-auto bg-orange-100 text-orange-700 border-orange-300">
            Family Shelter
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Head of Household Toggle */}
        <div className="flex items-center gap-3 p-3 bg-background rounded-lg border">
          <input
            type="checkbox"
            id="head-of-household"
            checked={isHeadOfHousehold}
            onChange={(e) => isEditing && handleToggleHeadOfHousehold(e.target.checked)}
            disabled={!isEditing}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label htmlFor="head-of-household" className="cursor-pointer">
            <span className="font-medium">Head of Household</span>
            <span className="text-sm text-muted-foreground block">
              This client is the primary contact for the family
            </span>
          </Label>
        </div>

        {/* Partner Section */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            Partner Name
          </Label>
          {isEditing ? (
            <Input
              value={partnerName}
              onChange={(e) => handleUpdatePartnerName(e.target.value)}
              placeholder="Enter partner's name (if applicable)"
            />
          ) : (
            <p className="text-sm p-2 bg-background rounded border">
              {client.partnerName || 'No partner listed'}
            </p>
          )}
        </div>

        {/* Children Section */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Baby className="h-4 w-4 text-muted-foreground" />
            Children
            {children.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {children.length}
              </Badge>
            )}
          </Label>

          {/* Existing Children List */}
          {children.length > 0 ? (
            <div className="space-y-2">
              {children.map((child, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-3 bg-background rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <Baby className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium">{child.name}</p>
                      {child.age && (
                        <p className="text-xs text-muted-foreground">Age: {child.age}</p>
                      )}
                    </div>
                  </div>
                  {isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveChild(idx)}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground p-3 bg-background rounded border">
              No children listed
            </p>
          )}

          {/* Add Child Form */}
          {isEditing && (
            <div className="p-3 border border-dashed border-orange-300 rounded-lg bg-orange-50">
              <Label className="text-sm font-medium mb-2 block">Add Child</Label>
              <div className="flex gap-2">
                <Input
                  value={newChildName}
                  onChange={(e) => setNewChildName(e.target.value)}
                  placeholder="Child's name"
                  className="flex-1"
                />
                <Input
                  type="number"
                  value={newChildAge}
                  onChange={(e) => setNewChildAge(e.target.value)}
                  placeholder="Age"
                  className="w-20"
                  min="0"
                  max="18"
                />
                <Button 
                  onClick={handleAddChild}
                  disabled={!newChildName.trim()}
                  size="sm"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Save Changes Button */}
        {isEditing && onSaveChanges && (
          <Button 
            onClick={onSaveChanges} 
            disabled={isSaving}
            className="w-full"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}

        {/* Info Note */}
        <p className="text-xs text-muted-foreground bg-orange-100 p-2 rounded">
          ℹ️ During check-in/out, staff will be prompted to confirm each child's presence.
          Missing children will trigger automatic notifications to supervisors.
        </p>
      </CardContent>
    </Card>
  );
};
